/**
 * Pure collapsing-key computation utilities for the DataGrid single-child
 * group-hiding feature.
 *
 * Extracted from ProductsRowGroupingDataGrid.tsx so this logic can be unit
 * tested independently of React/MUI. The component's IIFE is replaced by a
 * call to computeCollapsingKeys().
 *
 * Collapsing Rules (Implementation Rules 1-8):
 *   Rule 1: A level with exactly 1 child value collapses — its key is added to
 *            singleChildGroupKeys. MUI receives height:0 and lgm-hidden-group-row.
 *   Rule 2: A level with 2+ child values is visible — its key is NOT added.
 *   Rule 3: Values from ancestor levels must not appear on child rows (enforced
 *            in renderCell, not here).
 *   Rule 4: Leaf rows show only data column values (enforced in renderCell).
 *   Rule 5: Duplicate leaf rows across visible columns are deduplicated
 *            (handled by deduplicateRows.ts).
 *   Rule 6: Rules cascade — the entire chain is evaluated level-by-level.
 *   Rule 7: Default state is fully collapsed; auto-expand applies when
 *            ≤ AUTO_EXPAND_MAX_PRODUCTS distinct products exist.
 *   Rule 8: User collapse is permanent for the session (handled in component).
 *
 * Key format (matches MUI auto-generated row ID suffix):
 *   depth 0 : "field0/val0"
 *   depth 1 : "field0/val0-field1/val1"
 *   depth n : "field0/val0-…-fieldN/valN"
 *
 * Null-field handling: rows with null (or undefined) at a grouping level are
 * placed directly under the last non-null ancestor by MUI. We mirror this
 * behaviour by computing an effectiveFields list (non-null values only) per
 * row so generated keys match the auto-generated row IDs that MUI creates.
 */

/** Auto-expand threshold: ≤ this many distinct products → all groups expand on load. */
export const AUTO_EXPAND_MAX_PRODUCTS = 10;

export interface CollapsingKeysResult {
  /**
   * Group keys with exactly one child value at that depth.
   * These rows are rendered at height 0 and receive the lgm-hidden-group-row
   * CSS class so they are truly invisible and non-interactive.
   */
  singleChildGroupKeys: Set<string>;
  /**
   * Depth-0 group keys where the *entire* descendant chain is single-child,
   * meaning the product has exactly one deduplicated leaf row. These groups are
   * hidden like other single-child groups so the leaf renders the product name
   * inline in the depth-0 column as a single flat row (no chevron, no indent).
   */
  singleLeafProductKeys: Set<string>;
}

/**
 * Compute singleChildGroupKeys and singleLeafProductKeys from a flat row array
 * and an active grouping model.
 *
 * @param rows         Deduplicated flat product rows (Record<string, unknown>).
 * @param groupingModel  Ordered list of grouping field names (e.g. SOURCING model).
 */
export function computeCollapsingKeys(
  rows: Record<string, unknown>[],
  groupingModel: string[]
): CollapsingKeysResult {
  const result = new Set<string>();

  if (rows.length === 0 || groupingModel.length === 0) {
    return { singleChildGroupKeys: result, singleLeafProductKeys: new Set() };
  }

  // ── Phase 1: count distinct children per group key ──────────────────────────
  //
  // For each row, compute the effective field list (fields whose value is non-null
  // for that row). Walk down each depth and record the set of distinct child
  // values at that depth. A "child" at the last effective field is the row index
  // (a unique per-row sentinel); at shallower depths it is the next field's value.
  const parentToDistinctChildren = new Map<string, Set<unknown>>();

  rows.forEach((row, rowIndex) => {
    const effectiveFields = groupingModel.filter(f => row[f] != null);
    for (let d = 0; d < effectiveFields.length; d++) {
      const key = effectiveFields
        .slice(0, d + 1)
        .map(f => `${f}/${String(row[f])}`)
        .join('-');

      if (!parentToDistinctChildren.has(key)) {
        parentToDistinctChildren.set(key, new Set());
      }
      const childSet = parentToDistinctChildren.get(key);
      if (!childSet) return;
      if (d + 1 < effectiveFields.length) {
        // Next non-null field's value is the child
        childSet.add(row[effectiveFields[d + 1]]);
      } else {
        // Leaf level: use rowIndex as unique per-row identifier
        childSet.add(rowIndex);
      }
    }
  });

  // Keys with exactly 1 distinct child → single-child (hidden)
  parentToDistinctChildren.forEach((children, key) => {
    if (children.size === 1) result.add(key);
  });

  // ── Phase 2: identify single-leaf products ───────────────────────────────────
  //
  // A product's depth-0 key enters singleLeafProductKeys when every key in its
  // effective chain is already in singleChildGroupKeys. Such a product has exactly
  // one leaf row — all grouping levels collapse all the way down to a single flat
  // row. The depth-0 group row is also hidden; the leaf renders the product name
  // inline in the depth-0 grouping column.
  const singleLeafResult = new Set<string>();

  rows.forEach(row => {
    const ef = groupingModel.filter(f => row[f] != null);
    if (ef.length === 0) return;

    const isFullySingleChain = ef.every((_, d) => {
      const chainKey = ef
        .slice(0, d + 1)
        .map(f => `${f}/${String(row[f])}`)
        .join('-');
      return result.has(chainKey);
    });

    if (isFullySingleChain) {
      singleLeafResult.add(`${ef[0]}/${String(row[ef[0]])}`);
    }
  });

  return {
    singleChildGroupKeys: result,
    singleLeafProductKeys: singleLeafResult,
  };
}

/**
 * Compute whether the auto-expand threshold is met.
 * When true, all group rows should expand on load (Rule 7 auto-expand exception).
 *
 * @param rows      Deduplicated flat product rows.
 * @param threshold  Number of distinct products at or below which auto-expand
 *                   fires. Defaults to AUTO_EXPAND_MAX_PRODUCTS (10).
 */
export function computeShouldAutoExpand(
  rows: Record<string, unknown>[],
  threshold = AUTO_EXPAND_MAX_PRODUCTS
): boolean {
  const distinctProductIds = new Set(
    rows.map(r => r['product_id']).filter(id => id != null)
  );
  return distinctProductIds.size <= threshold;
}

/**
 * Build the group key string for a chain of (field, value) pairs.
 * Convenience helper for use in tests and assertions.
 *
 * Example:
 *   buildGroupKey([['preferred_product_name', 'Aspirin'], ['grade_name', 'API']])
 *   → "preferred_product_name/Aspirin-grade_name/API"
 */
export function buildGroupKey(
  segments: Array<[field: string, value: unknown]>
): string {
  return segments.map(([f, v]) => `${f}/${String(v)}`).join('-');
}
